import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { AnnotationList } from '../components/AnnotationList.js';
import { PassageView } from '../components/PassageView.js';
import { QuestionControl, normalizePromptHtml } from '../components/QuestionControl.js';
function formatClock(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
export function PracticePage() {
    const { examId } = useParams({ from: '/practice/$examId' });
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const examQuery = useQuery({ queryKey: ['exam', examId], queryFn: () => api.exam(examId) });
    const attemptQuery = useQuery({
        queryKey: ['attempt-start', examId],
        queryFn: () => api.startAttempt(examId),
        staleTime: Number.POSITIVE_INFINITY,
        gcTime: 0,
        retry: false,
    });
    const [answers, setAnswers] = useState({});
    const [flagged, setFlagged] = useState([]);
    const [timeSpent, setTimeSpent] = useState({});
    const [currentId, setCurrentId] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const initializedRef = useRef(null);
    const passageRef = useRef(null);
    const exam = examQuery.data?.exam;
    const attemptId = attemptQuery.data?.attemptId ?? null;
    const questions = useMemo(() => exam?.questions ?? [], [exam]);
    const groups = useMemo(() => exam?.questionGroups ?? [], [exam]);
    // ---- 文章标注（只作用于 passage；精读页给题干/选项写的标注在这里不参与渲染） ----
    const annotationsQuery = useQuery({
        queryKey: ['annotations', examId],
        queryFn: () => api.annotations(examId),
    });
    const annotations = useMemo(() => (annotationsQuery.data?.items ?? []).filter((item) => item.anchorKind === 'passage'), [annotationsQuery.data]);
    const createAnnotations = useMutation({
        mutationFn: async (drafts) => {
            for (const draft of drafts) {
                await api.createAnnotation({
                    examId,
                    anchorKind: draft.anchorKind,
                    anchorId: draft.anchorId,
                    surface: 'practice',
                    sentenceIndex: draft.sentenceIndex,
                    startOffset: draft.startOffset,
                    endOffset: draft.endOffset,
                    kind: draft.color ? 'highlight' : 'note',
                    color: draft.color,
                    selectedText: draft.selectedText,
                    note: draft.note,
                });
            }
        },
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['annotations', examId] }),
    });
    const updateAnnotation = useMutation({
        mutationFn: (input) => api.updateAnnotation(input.id, input.patch),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['annotations', examId] }),
    });
    const deleteAnnotation = useMutation({
        mutationFn: (id) => api.deleteAnnotation(id),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['annotations', examId] }),
    });
    // 恢复草稿（只在拿到 attempt 时执行一次）
    useEffect(() => {
        const started = attemptQuery.data;
        if (!started || initializedRef.current === started.attemptId)
            return;
        initializedRef.current = started.attemptId;
        if (started.draft) {
            const restored = {};
            for (const [key, value] of Object.entries(started.draft.answers)) {
                restored[key] = value ?? '';
            }
            setAnswers(restored);
            setFlagged(started.draft.flagged);
            setTimeSpent(started.draft.timeSpent);
        }
        setCurrentId(questions[0]?.id ?? null);
    }, [attemptQuery.data, questions]);
    // 计时器：累计总用时，并把这一秒记到当前题上
    useEffect(() => {
        if (!attemptId)
            return;
        const timer = window.setInterval(() => {
            setElapsed((value) => value + 1);
            setCurrentId((id) => {
                if (id) {
                    setTimeSpent((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1000 }));
                }
                return id;
            });
        }, 1000);
        return () => window.clearInterval(timer);
    }, [attemptId]);
    const payload = useCallback(() => {
        return { answers, flagged, timeSpent };
    }, [answers, flagged, timeSpent]);
    const payloadRef = useRef(payload);
    payloadRef.current = payload;
    // 草稿自动保存：每 5 秒
    useEffect(() => {
        if (!attemptId)
            return;
        const timer = window.setInterval(() => {
            void api
                .saveDraft(attemptId, payloadRef.current())
                .then((result) => setSavedAt(result.savedAt))
                .catch(() => undefined);
        }, 5000);
        return () => window.clearInterval(timer);
    }, [attemptId]);
    const submit = useMutation({
        mutationFn: async () => {
            if (!attemptId)
                throw new Error('作答尚未开始');
            return api.submitAttempt(attemptId, elapsed * 1000, payloadRef.current());
        },
        onSuccess: (review) => {
            void queryClient.invalidateQueries({ queryKey: ['exams'] });
            void queryClient.invalidateQueries({ queryKey: ['attempts'] });
            void queryClient.invalidateQueries({ queryKey: ['mistakes'] });
            void queryClient.invalidateQueries({ queryKey: ['stats'] });
            void navigate({ to: '/review/$attemptId', params: { attemptId: review.attemptId } });
        },
    });
    const setAnswer = useCallback((questionId, value) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    }, []);
    const toggleFlag = useCallback((questionId) => {
        setFlagged((prev) => prev.includes(questionId) ? prev.filter((id) => id !== questionId) : [...prev, questionId]);
    }, []);
    const move = useCallback((delta) => {
        if (questions.length === 0)
            return;
        const index = questions.findIndex((question) => question.id === currentId);
        const next = Math.min(Math.max((index === -1 ? 0 : index) + delta, 0), questions.length - 1);
        const target = questions[next];
        if (!target)
            return;
        setCurrentId(target.id);
        const element = document.getElementById(`question-${target.id}`);
        element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, [currentId, questions]);
    // 键盘流：数字/字母选项、n/p 翻题、f 标记、Ctrl+Enter 交卷
    useEffect(() => {
        function handleKeyDown(event) {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                setConfirmOpen(true);
                return;
            }
            const target = event.target;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
                if (event.key === 'Enter')
                    target.blur();
                return;
            }
            if (event.key === 'n') {
                move(1);
                return;
            }
            if (event.key === 'p') {
                move(-1);
                return;
            }
            if (event.key === 'f' && currentId) {
                toggleFlag(currentId);
                return;
            }
            const question = questions.find((item) => item.id === currentId);
            if (!question)
                return;
            const key = event.key.toUpperCase();
            if (question.kind === 'tfng' || question.kind === 'ynng') {
                if (key === 'T')
                    setAnswer(question.id, 'TRUE');
                if (key === 'F')
                    setAnswer(question.id, 'FALSE');
                if (key === 'N' || key === 'G')
                    setAnswer(question.id, 'NOT GIVEN');
                return;
            }
            const options = question.kind === 'matching' || question.kind === 'matching_headings'
                ? (groups.find((group) => group.questionIds.includes(question.id))?.options ?? [])
                : (question.options ?? []);
            const index = 'ABCD'.indexOf(key);
            if (index >= 0 && options[index]) {
                setAnswer(question.id, options[index].key);
            }
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentId, groups, move, questions, setAnswer, toggleFlag]);
    const answeredCount = questions.filter((question) => (answers[question.id] ?? '').trim() !== '').length;
    const unanswered = questions.filter((question) => (answers[question.id] ?? '').trim() === '');
    if (examQuery.isPending || attemptQuery.isPending) {
        return (_jsxs("main", { className: "page", children: [_jsx("div", { className: "skeleton", style: { height: 40, width: 320, marginBottom: 24 } }), _jsxs("div", { className: "practice-grid", children: [_jsx("div", { className: "skeleton", style: { height: 520 } }), _jsx("div", { className: "skeleton", style: { height: 520 } })] })] }));
    }
    if (examQuery.isError || !exam) {
        return (_jsx("main", { className: "page", children: _jsxs("div", { className: "error-box", role: "alert", children: ["\u9898\u76EE\u52A0\u8F7D\u5931\u8D25\uFF1A", examQuery.error instanceof Error ? examQuery.error.message : '未知错误'] }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "practice-topbar", children: [_jsx("span", { className: "practice-topbar__title", children: exam.meta.title }), _jsx("span", { className: "badge badge--accent", children: exam.meta.category }), _jsxs("span", { className: "badge", children: ["\u7B2C ", attemptQuery.data?.attemptIndex ?? 1, " \u6B21\u4F5C\u7B54"] }), _jsxs("span", { className: "counter", children: ["\u5DF2\u7B54 ", answeredCount, " / ", questions.length] }), _jsx("span", { className: "counter", "aria-label": "\u7528\u65F6", children: formatClock(elapsed) }), _jsx("span", { className: "faint", style: { fontSize: 12 }, children: savedAt ? `草稿已保存 ${new Date(savedAt).toLocaleTimeString()}` : '草稿每 5 秒自动保存' }), _jsx("div", { className: "practice-topbar__spacer" }), _jsx(Link, { to: "/close-reading/$examId", params: { examId }, className: "btn btn--ghost btn--sm", children: "\u7CBE\u8BFB\u8FD9\u4E00\u7BC7" }), _jsx("button", { type: "button", className: "btn btn--primary btn--sm", onClick: () => setConfirmOpen(true), children: "\u4EA4\u5377" })] }), _jsx("main", { className: "page page--wide", children: _jsxs("div", { className: "practice-grid", children: [_jsxs("div", { className: "stack", children: [_jsx("section", { className: "panel", children: _jsx(PassageView, { exam: exam, annotations: annotations, containerRef: passageRef, onCreate: (drafts) => createAnnotations.mutate(drafts), onUpdate: (id, patch) => updateAnnotation.mutate({ id, patch }), onDelete: (id) => deleteAnnotation.mutate(id) }) }), _jsxs("section", { className: "panel", children: [_jsx("h3", { style: { marginBottom: 12 }, children: "\u672C\u5377\u6807\u6CE8" }), _jsx(AnnotationList, { annotations: annotations, containerRef: passageRef, onDelete: (id) => deleteAnnotation.mutate(id) })] })] }), _jsxs("section", { className: "sticky-panel", "data-annotation-scope": "forbidden", children: [groups.map((group) => (_jsxs("div", { className: "group", children: [group.instructionsHtml ? (_jsx("div", { className: "group__instructions", dangerouslySetInnerHTML: { __html: group.instructionsHtml } })) : null, group.questionIds.map((questionId) => {
                                            const question = questions.find((item) => item.id === questionId);
                                            if (!question)
                                                return null;
                                            const isCurrent = currentId === question.id;
                                            const isFlagged = flagged.includes(question.id);
                                            return (_jsxs("div", { id: `question-${question.id}`, className: [
                                                    'question',
                                                    isCurrent ? 'is-current' : '',
                                                    isFlagged ? 'is-flagged' : '',
                                                ]
                                                    .filter(Boolean)
                                                    .join(' '), onFocus: () => setCurrentId(question.id), onClick: () => setCurrentId(question.id), children: [_jsxs("div", { className: "question__head", children: [_jsx("span", { className: "question__number", children: question.displayNumber }), _jsx("div", { className: "question__prompt", dangerouslySetInnerHTML: { __html: normalizePromptHtml(question.promptHtml) } }), _jsx("button", { type: "button", className: "btn btn--ghost btn--sm", "aria-pressed": isFlagged, onClick: (event) => {
                                                                    event.stopPropagation();
                                                                    toggleFlag(question.id);
                                                                }, children: isFlagged ? '取消标记' : '标记' })] }), _jsx(QuestionControl, { question: question, group: group, value: answers[question.id] ?? '', onChange: setAnswer })] }, question.id));
                                        })] }, group.id))), _jsxs("div", { className: "panel", style: { marginTop: 16 }, children: [_jsxs("div", { className: "row-inline", style: { marginBottom: 12, justifyContent: 'space-between' }, children: [_jsx("strong", { children: "\u7B54\u9898\u5361" }), _jsxs("span", { className: "faint", style: { fontSize: 12 }, children: ["\u5FEB\u6377\u952E\uFF1A", _jsx("span", { className: "kbd", children: "A" }), "-", _jsx("span", { className: "kbd", children: "D" }), " \u9009\u9879 \u00B7", ' ', _jsx("span", { className: "kbd", children: "T" }), "/", _jsx("span", { className: "kbd", children: "F" }), "/", _jsx("span", { className: "kbd", children: "N" }), " \u5224\u65AD \u00B7 ", _jsx("span", { className: "kbd", children: "n" }), "/", _jsx("span", { className: "kbd", children: "p" }), " \u7FFB\u9898 \u00B7 ", _jsx("span", { className: "kbd", children: "f" }), " \u6807\u8BB0 \u00B7", ' ', _jsx("span", { className: "kbd", children: "Ctrl" }), "+", _jsx("span", { className: "kbd", children: "Enter" }), " \u4EA4\u5377"] })] }), _jsx("div", { className: "answer-sheet", children: questions.map((question) => {
                                                const isAnswered = (answers[question.id] ?? '').trim() !== '';
                                                return (_jsx("button", { type: "button", className: [
                                                        'sheet-cell',
                                                        isAnswered ? 'is-answered' : '',
                                                        flagged.includes(question.id) ? 'is-flagged' : '',
                                                        currentId === question.id ? 'is-current' : '',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' '), onClick: () => {
                                                        setCurrentId(question.id);
                                                        document
                                                            .getElementById(`question-${question.id}`)
                                                            ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                                                    }, children: question.displayNumber }, question.id));
                                            }) }), _jsxs("div", { className: "sheet-legend", children: [_jsx("span", { children: "\u5DF2\u7B54\uFF1A\u586B\u8272" }), _jsx("span", { children: "\u6807\u8BB0\uFF1A\u5E95\u90E8\u6A2A\u7EBF" }), _jsx("span", { children: "\u5F53\u524D\uFF1A\u5916\u6846" })] })] })] })] }) }), confirmOpen ? (_jsx("div", { className: "overlay", role: "dialog", "aria-modal": "true", "aria-labelledby": "submit-title", children: _jsxs("div", { className: "dialog", children: [_jsx("h3", { id: "submit-title", children: "\u786E\u8BA4\u4EA4\u5377\uFF1F" }), _jsx("p", { className: "muted", style: { marginTop: 12 }, children: unanswered.length === 0
                                ? '所有题目都已作答。'
                                : `还有 ${unanswered.length} 题没有作答：第 ${unanswered
                                    .map((question) => question.displayNumber)
                                    .join('、')} 题。` }), _jsx("p", { className: "faint", style: { fontSize: 12 }, children: "\u4EA4\u5377\u540E\u672C\u5377\u4F1A\u5199\u5165\u8BB0\u5F55\uFF0C\u5E76\u81EA\u52A8\u628A\u9519\u9898\u6536\u8FDB\u9519\u9898\u672C\u3002" }), submit.isError ? (_jsxs("p", { className: "field__error", role: "alert", children: ["\u4EA4\u5377\u5931\u8D25\uFF1A", submit.error instanceof Error ? submit.error.message : '未知错误'] })) : null, _jsxs("div", { className: "dialog__actions", children: [_jsx("button", { type: "button", className: "btn", onClick: () => setConfirmOpen(false), children: "\u7EE7\u7EED\u4F5C\u7B54" }), _jsx("button", { type: "button", className: "btn btn--primary", disabled: submit.isPending, onClick: () => submit.mutate(), children: submit.isPending ? '判分中…' : '确认交卷' })] })] }) })) : null] }));
}
//# sourceMappingURL=PracticePage.js.map